import { useEffect, useRef } from 'react';

// Subscribes to a device's latest-sample Server-Sent Events stream and invokes
// `onMessage(eventType, data)` for each event. Uses fetch + AbortController (so we can
// send the Bearer token, which EventSource can't), with exponential-backoff reconnect
// and an inactivity watchdog. Cleans up on unmount or when the device changes.

const BASE_RETRY_DELAY_MS = 1000;
const MAX_RETRY_DELAY_MS = 30000;
const INACTIVITY_TIMEOUT_MS = 45000;

const baseUrl = () => process.env.REACT_APP_TRADER_API_URL || 'http://localhost:4000/v1/retailer';

const useSSELatestSample = (deviceId, onMessage, onError) => {
    const onMessageRef = useRef(onMessage);
    const onErrorRef = useRef(onError);
    onMessageRef.current = onMessage;
    onErrorRef.current = onError;

    useEffect(() => {
        if (!deviceId) {
            return undefined;
        }

        const endpoint = `${baseUrl()}/devices/${encodeURIComponent(deviceId)}/sse/samples`;
        let unmounted = false;
        let controller = null;
        let retryTimer = null;
        let inactivityTimer = null;
        let retryCount = 0;

        const clearTimers = () => {
            if (retryTimer) {
                clearTimeout(retryTimer);
            }
            if (inactivityTimer) {
                clearTimeout(inactivityTimer);
            }
        };

        const scheduleReconnect = () => {
            if (unmounted) {
                return;
            }
            const delay = Math.min(BASE_RETRY_DELAY_MS * 2 ** retryCount, MAX_RETRY_DELAY_MS) * (0.9 + Math.random() * 0.2);
            retryCount += 1;
            if (retryTimer) {
                clearTimeout(retryTimer);
            }
            retryTimer = setTimeout(connect, delay);
        };

        const resetInactivity = () => {
            if (inactivityTimer) {
                clearTimeout(inactivityTimer);
            }
            inactivityTimer = setTimeout(() => {
                if (controller) {
                    controller.abort();
                }
                scheduleReconnect();
            }, INACTIVITY_TIMEOUT_MS);
        };

        function connect() {
            if (unmounted) {
                return;
            }
            controller = new AbortController();
            resetInactivity();

            fetch(endpoint, {
                headers: {
                    Authorization: `Bearer ${window.auth_token || 'dummy_token'}`,
                    Accept: 'text/event-stream',
                    'Cache-Control': 'no-cache',
                },
                signal: controller.signal,
            })
                .then(response => {
                    if (!response.ok || !response.body) {
                        clearTimers();
                        onErrorRef.current?.(new Error(`SSE connection failed (${response.status})`));
                        scheduleReconnect();
                        return;
                    }
                    retryCount = 0;
                    const reader = response.body.getReader();
                    const decoder = new TextDecoder('utf-8');
                    let buffer = '';

                    const read = () => {
                        reader
                            .read()
                            .then(({ done, value }) => {
                                if (done) {
                                    clearTimers();
                                    scheduleReconnect();
                                    return;
                                }
                                resetInactivity();
                                buffer += decoder.decode(value, { stream: true });
                                // SSE events are separated by a blank line.
                                const events = buffer.split('\n\n');
                                buffer = events.pop() || '';
                                for (const evt of events) {
                                    let eventType = 'message';
                                    let data = null;
                                    for (const line of evt.split('\n')) {
                                        if (line.startsWith('event:')) {
                                            eventType = line.slice(6).trim();
                                        } else if (line.startsWith('data:')) {
                                            try {
                                                data = JSON.parse(line.slice(5).trim());
                                            } catch (e) {
                                                data = null;
                                            }
                                        }
                                    }
                                    onMessageRef.current(eventType, data);
                                }
                                read();
                            })
                            .catch(err => {
                                clearTimers();
                                if (err.name !== 'AbortError') {
                                    onErrorRef.current?.(err);
                                    scheduleReconnect();
                                }
                            });
                    };

                    read();
                })
                .catch(err => {
                    clearTimers();
                    if (err.name !== 'AbortError') {
                        onErrorRef.current?.(err);
                        scheduleReconnect();
                    }
                });
        }

        connect();

        return () => {
            unmounted = true;
            clearTimers();
            if (controller) {
                controller.abort();
            }
        };
    }, [deviceId]);
};

export default useSSELatestSample;
